import React, { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import timeClockTheme from "./timeClockTheme";

const buildBreaks = (clockIn, clockOut, unpaidMinutes, paidMinutes) => {
  const start = new Date(clockIn).getTime();
  const end = new Date(clockOut).getTime();
  const durations = [
    { minutes: Number(paidMinutes) || 0, is_paid: true },
    { minutes: Number(unpaidMinutes) || 0, is_paid: false },
  ].filter((item) => item.minutes > 0);
  const totalMilliseconds = durations.reduce(
    (sum, item) => sum + item.minutes * 60000,
    0
  );
  let cursor = start + Math.max(0, (end - start - totalMilliseconds) / 2);
  return durations.map((item) => {
    const breakStart = cursor;
    cursor += item.minutes * 60000;
    return {
      clocked_in_at: breakStart,
      clocked_out_at: cursor,
      is_paid: item.is_paid,
    };
  });
};

const requestBreakMinutes = (request, isPaid) =>
  (request?.proposed_breaks || [])
    .filter((item) => Boolean(item.is_paid) === isPaid)
    .reduce(
      (total, item) =>
        total + (item.clocked_out_at - item.clocked_in_at) / 60000,
      0
    );

const existingBreaksForRequest = (entry, changeRequest) =>
  (changeRequest?.proposed_breaks || entry?.timecard_breaks || []).map(
    ({ uuid, clocked_in_at, clocked_out_at, is_paid }) => ({
      ...(uuid ? { uuid } : {}),
      clocked_in_at,
      clocked_out_at,
      is_paid,
    })
  );

const brandedFieldClasses =
  "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-100";

function TimecardChangeRequestModal({
  open,
  onClose,
  entry,
  changeRequest,
  onSubmit,
  rolesList,
  paidBreakTrackingEnabled,
  defaultDate,
  timeEntriesRoleSelectionEnabled,
}) {
  const [clockIn, setClockIn] = useState(null);
  const [clockOut, setClockOut] = useState(null);
  const [unpaidMinutes, setUnpaidMinutes] = useState(0);
  const [paidMinutes, setPaidMinutes] = useState(0);
  const [roleId, setRoleId] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [breaksEdited, setBreaksEdited] = useState(false);

  const shiftHours =
    clockIn && clockOut && clockOut > clockIn
      ? (clockOut.getTime() - clockIn.getTime()) / 3600000
      : null;
  const paidHours =
    shiftHours === null
      ? null
      : Math.max(0, shiftHours - (Number(unpaidMinutes) || 0) / 60);

  useEffect(() => {
    if (!open) return;
    const selectedDateStart = defaultDate
      ? new Date(`${defaultDate}T09:00:00`).getTime()
      : Date.now();
    const defaultClockIn =
      changeRequest?.proposed_clocked_in_at || entry?.clocked_in_at || selectedDateStart;
    const defaultClockOut =
      changeRequest?.proposed_clocked_out_at ||
      entry?.clocked_out_at ||
      selectedDateStart + 8 * 60 * 60 * 1000;
    setClockIn(new Date(defaultClockIn));
    setClockOut(new Date(defaultClockOut));
    setUnpaidMinutes(
      changeRequest ? requestBreakMinutes(changeRequest, false) : entry?.break_minutes || 0
    );
    setPaidMinutes(
      changeRequest ? requestBreakMinutes(changeRequest, true) : entry?.paid_break_minutes || 0
    );
    setRoleId(changeRequest?.role?.id || entry?.role?.id || "");
    setNote(changeRequest?.employee_note || "");
    setError("");
    setBreaksEdited(false);
  }, [changeRequest, defaultDate, entry, open]);

  if (!open) return null;

  const handleSubmit = async () => {
    const clockedInAt = new Date(clockIn);
    const clockedOutAt = new Date(clockOut);
    if (
      !clockIn ||
      !clockOut ||
      Number.isNaN(clockedInAt.getTime()) ||
      Number.isNaN(clockedOutAt.getTime()) ||
      clockedOutAt <= clockedInAt
    ) {
      setError("Clock out must be after clock in.");
      return;
    }
    if (!note.trim()) {
      setError("Please explain why this change is needed.");
      return;
    }
    if (timeEntriesRoleSelectionEnabled && !roleId) {
      setError("Please select a role.");
      return;
    }
    const shiftMinutes = (clockedOutAt - clockedInAt) / 60000;
    if ((Number(unpaidMinutes) || 0) + (Number(paidMinutes) || 0) >= shiftMinutes) {
      setError("Total break time must be shorter than the shift.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onSubmit({
        timecard_change_request_uuid: changeRequest?.uuid || null,
        request_type: changeRequest?.request_type || (entry?.uuid ? "edit" : "create"),
        timecard_uuid: changeRequest?.timecard_uuid || entry?.uuid || null,
        clocked_in_at: clockedInAt.getTime(),
        clocked_out_at: clockedOutAt.getTime(),
        timecard_breaks: breaksEdited
          ? buildBreaks(
              clockedInAt,
              clockedOutAt,
              unpaidMinutes,
              paidMinutes
            )
          : existingBreaksForRequest(entry, changeRequest),
        role_id: roleId || null,
        employee_note: note.trim(),
      });
      onClose();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.system?.message ||
          requestError?.response?.data?.message ||
          requestError?.response?.data?.error ||
          "Unable to submit the request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={timeClockTheme}>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
        <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {changeRequest
              ? "Edit time entry request"
              : entry?.uuid
              ? "Request a time entry edit"
              : "Request a missing time entry"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Your manager will review these proposed times before anything changes.
          </p>
        </div>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-1">
              <div>
                <p className="text-sm text-slate-600">Clock in</p>
                <div className="mt-1">
                  <DateTimePicker
                    views={["month", "day", "hours", "minutes"]}
                    value={clockIn}
                    onChange={setClockIn}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            height: "45px",
                            backgroundColor: "white",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                          },
                        },
                      },
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-600">Clock out</p>
                <div className="mt-1">
                  <DateTimePicker
                    views={["month", "day", "hours", "minutes"]}
                    value={clockOut}
                    onChange={setClockOut}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            height: "45px",
                            backgroundColor: "white",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                          },
                        },
                      },
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </div>
              </div>
            </div>
          </LocalizationProvider>
          <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">
                  Proposed shift length
                </p>
                <p className="mt-1 text-xl font-bold text-slate-900">
                  {shiftHours === null ? "—" : `${shiftHours.toFixed(2)} hours`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Paid after unpaid breaks</p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {paidHours === null ? "—" : `${paidHours.toFixed(2)} hours`}
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="text-sm text-slate-600">
              Unpaid break minutes
              <input
                type="number"
                min="0"
                value={unpaidMinutes}
                onChange={(event) => {
                  setUnpaidMinutes(event.target.value);
                  setBreaksEdited(true);
                }}
                className={brandedFieldClasses}
              />
            </label>
            {paidBreakTrackingEnabled && (
              <label className="text-sm text-slate-600">
                Paid break minutes
                <input
                  type="number"
                  min="0"
                  value={paidMinutes}
                  onChange={(event) => {
                    setPaidMinutes(event.target.value);
                    setBreaksEdited(true);
                  }}
                  className={brandedFieldClasses}
                />
              </label>
            )}
          </div>
          {timeEntriesRoleSelectionEnabled && rolesList?.length > 0 && (
            <label className="block text-sm text-slate-600">
              Role
              <select
                value={roleId}
                onChange={(event) => setRoleId(event.target.value)}
                className={brandedFieldClasses}
              >
                <option value="">Select a role</option>
                {rolesList.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.role_name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="block text-sm text-slate-600">
            Explanation
            <textarea
              rows="3"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="What should be corrected?"
              className={brandedFieldClasses}
            />
          </label>
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={handleSubmit}
            className="rounded-full bg-violet-700 px-5 py-2 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 disabled:opacity-50"
          >
            {submitting
              ? "Saving…"
              : changeRequest
              ? "Save request"
              : "Submit request"}
          </button>
        </div>
        </div>
      </div>
    </ThemeProvider>
  );
}

export default TimecardChangeRequestModal;
